import { useState, useMemo } from 'react';
import type { ApiEndpoints, LDNDStatusEvent } from '@avisoma/shared';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { LDNDTable } from '../../components/reconstructedDocument/extractionTables/LDNDTable';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { SourceCard } from './SourceCard';

interface LDNDEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

function joinUnits(parts: Array<string | number | undefined>): string {
  return parts.filter((part) => part != null && part !== '').join(' · ') || '—';
}

export function LDNDEvents({ response }: LDNDEventsProps) {
  const data = response.item.data?.ldndEvents;
  const events = data?.ldndStatusEvents;
  const header = data?.header;
  const [inspected, setInspected] = useState<LDNDStatusEvent | null>(null);

  const pageIdToPosition = useMemo(() => {
    const map = new Map<string, number>();
    data?.pageIds.forEach((pageId, index) => map.set(pageId, index + 1));
    return map;
  }, [data?.pageIds]);

  if (!data || !events) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SourceCard sources={[{ esn: header?.engineSN, documentId: data.documentId, pageIds: data.pageIds }]} />

      <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">LDND Events</h2>}>
        <DescriptionList>
          {header && (
            <>
              <DescriptionTerm>Header</DescriptionTerm>
              <DescriptionDetails>
                {joinUnits([
                  header.msn ? `MSN ${header.msn}` : undefined,
                  header.registration ? `Reg ${header.registration}` : undefined,
                  header.date,
                  header.engineTSN != null ? `TSN ${header.engineTSN}` : undefined,
                  header.engineCSN != null ? `CSN ${header.engineCSN}` : undefined,
                ])}
              </DescriptionDetails>
            </>
          )}

          <DescriptionTerm>Task Count</DescriptionTerm>
          <DescriptionDetails>{events.length}</DescriptionDetails>
        </DescriptionList>
      </Card>

      <ExtractionTableCard label="LDND Events" rows={events} csvTitle="LDND Events" bodyHeight="project">
        <LDNDTable rows={events} pageIdToPosition={pageIdToPosition} onInspect={setInspected} />
      </ExtractionTableCard>

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="LDND row"
        data={inspected}
      />
    </div>
  );
}
